import React, { useState, useEffect } from 'react';
import BoxPlot from './BoxPlot';
import './ClubDistanceBoxPlot.css';

const ClubDistanceBoxPlot = () => {
  // Sample data - in a real app, this would come from your API/database
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('distance');

  useEffect(() => {
    // Simulate fetching data from an API
    const fetchData = async () => {
      try {
        // This is sample data - replace with actual API call
        const sampleData = [
          {
            club: 'Driver',
            shots: [
              { distance: 265, dispersion: 15, ballSpeed: 155, launchAngle: 12.5, spinRate: 2500 },
              { distance: 275, dispersion: 10, ballSpeed: 158, launchAngle: 13.0, spinRate: 2400 },
              { distance: 260, dispersion: 20, ballSpeed: 152, launchAngle: 12.0, spinRate: 2600 },
              { distance: 270, dispersion: 12, ballSpeed: 156, launchAngle: 12.8, spinRate: 2450 },
              { distance: 280, dispersion: 18, ballSpeed: 160, launchAngle: 13.2, spinRate: 2350 },
              { distance: 255, dispersion: 25, ballSpeed: 150, launchAngle: 11.8, spinRate: 2650 },
              { distance: 272, dispersion: 14, ballSpeed: 157, launchAngle: 12.9, spinRate: 2480 },
              { distance: 290, dispersion: 30, ballSpeed: 165, launchAngle: 13.5, spinRate: 2300 } // Outlier
            ]
          },
          {
            club: '3-Wood',
            shots: [
              { distance: 235, dispersion: 12, ballSpeed: 145, launchAngle: 13.5, spinRate: 3000 },
              { distance: 240, dispersion: 15, ballSpeed: 147, launchAngle: 14.0, spinRate: 2900 },
              { distance: 230, dispersion: 18, ballSpeed: 143, launchAngle: 13.0, spinRate: 3100 },
              { distance: 245, dispersion: 10, ballSpeed: 148, launchAngle: 14.2, spinRate: 2850 },
              { distance: 225, dispersion: 20, ballSpeed: 140, launchAngle: 12.8, spinRate: 3150 },
              { distance: 210, dispersion: 25, ballSpeed: 135, launchAngle: 12.0, spinRate: 3300 } // Outlier
            ]
          },
          {
            club: '5-Iron',
            shots: [
              { distance: 185, dispersion: 8, ballSpeed: 125, launchAngle: 16.0, spinRate: 5000 },
              { distance: 180, dispersion: 10, ballSpeed: 123, launchAngle: 15.5, spinRate: 5100 },
              { distance: 190, dispersion: 12, ballSpeed: 127, launchAngle: 16.5, spinRate: 4900 },
              { distance: 175, dispersion: 15, ballSpeed: 120, launchAngle: 15.0, spinRate: 5200 },
              { distance: 182, dispersion: 9, ballSpeed: 124, launchAngle: 15.8, spinRate: 5050 },
              { distance: 187, dispersion: 11, ballSpeed: 126, launchAngle: 16.2, spinRate: 4950 },
              { distance: 205, dispersion: 18, ballSpeed: 130, launchAngle: 17.0, spinRate: 4800 } // Outlier
            ]
          },
          {
            club: '7-Iron',
            shots: [
              { distance: 165, dispersion: 7, ballSpeed: 115, launchAngle: 18.0, spinRate: 6000 },
              { distance: 160, dispersion: 9, ballSpeed: 113, launchAngle: 17.5, spinRate: 6100 },
              { distance: 170, dispersion: 10, ballSpeed: 117, launchAngle: 18.5, spinRate: 5900 },
              { distance: 155, dispersion: 12, ballSpeed: 110, launchAngle: 17.0, spinRate: 6200 },
              { distance: 162, dispersion: 8, ballSpeed: 114, launchAngle: 17.8, spinRate: 6050 },
              { distance: 145, dispersion: 15, ballSpeed: 105, launchAngle: 16.5, spinRate: 6300 } // Outlier
            ]
          },
          {
            club: 'PW',
            shots: [
              { distance: 135, dispersion: 5, ballSpeed: 100, launchAngle: 24.0, spinRate: 8500 },
              { distance: 130, dispersion: 6, ballSpeed: 98, launchAngle: 23.5, spinRate: 8600 },
              { distance: 140, dispersion: 7, ballSpeed: 102, launchAngle: 24.5, spinRate: 8400 },
              { distance: 125, dispersion: 8, ballSpeed: 95, launchAngle: 23.0, spinRate: 8700 },
              { distance: 132, dispersion: 6, ballSpeed: 99, launchAngle: 23.8, spinRate: 8550 },
              { distance: 138, dispersion: 7, ballSpeed: 101, launchAngle: 24.2, spinRate: 8450 },
              { distance: 115, dispersion: 12, ballSpeed: 90, launchAngle: 22.0, spinRate: 8900 } // Outlier
            ]
          }
        ];

        setData(sampleData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const metrics = [
    { value: 'distance', label: 'Distance (yards)' },
    { value: 'dispersion', label: 'Dispersion (yards)' },
    { value: 'ballSpeed', label: 'Ball Speed (mph)' },
    { value: 'launchAngle', label: 'Launch Angle (°)' },
    { value: 'spinRate', label: 'Spin Rate (rpm)' }
  ];

  const getMetricLabel = (metric) => {
    const found = metrics.find(m => m.value === metric);
    return found ? found.label : '';
  };

  if (loading) {
    return <div className="loading">Loading club statistics...</div>;
  }

  return (
    <div className="club-boxplot-container">
      <div className="metric-selector">
        <label htmlFor="metric-select">Select Metric:</label>
        <select
          id="metric-select"
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value)}
        >
          {metrics.map(metric => (
            <option key={metric.value} value={metric.value}>
              {metric.label}
            </option>
          ))}
        </select>
      </div>

      <BoxPlot
        data={data}
        title={`Club ${getMetricLabel(selectedMetric)} Distribution`}
        xAxisLabel="Club"
        yAxisLabel={getMetricLabel(selectedMetric)}
        metric={selectedMetric}
      />

      <div className="boxplot-explanation">
        <h4>Understanding Box Plots</h4>
        <p>Box plots show the distribution of data through quartiles:</p>
        <ul>
          <li>The box represents the middle 50% of your shots (from 25th to 75th percentile)</li>
          <li>The line inside the box is the median (50th percentile)</li>
          <li>The whiskers extend to the minimum and maximum values within 1.5 times the interquartile range</li>
          <li>Points beyond the whiskers are outliers (shown as red dots)</li>
          <li>The orange dots represent the mean value for each club</li>
        </ul>
        <p>This visualization helps you understand your consistency with each club and identify outlier shots.</p>
      </div>
    </div>
  );
};

export default ClubDistanceBoxPlot;
